<template>
  <Transition name="gallop-fade">
    <div class="year-of-horse-loading" v-if="visible">
      <div class="bg-pattern"></div>

      <div class="content-wrapper">
        <div class="horse-logo">
          <div class="ring-light"></div>
          <svg class="horse-svg" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M858.6 346.4c-12.2-25.6-36.8-52.6-60.4-66.2-22.4-13-68.8-19.8-68.8-19.8s13.4-36.2 16.6-49.8c3.2-13.6-2.8-30.8-16.6-43.2-13.8-12.4-36.2-16.6-36.2-16.6s-15.4 33.2-27.8 45.6c-12.4 12.4-38.6 15.4-38.6 15.4s-19.8-22.4-43.2-25.6c-23.4-3.2-52.6 6-66.2 16.6-13.6 10.6-22.4 36.8-22.4 36.8s-30.8-6-52.6 6c-21.8 12-36.8 33.2-36.8 33.2s-19.8-9.2-43.2 0c-23.4 9.2-36.8 33.2-36.8 33.2l-16.6 66.2c0 0-33.2 13.6-49.8 36.8-16.6 23.2-16.6 52.6-16.6 52.6s-25.6 13.6-36.8 36.8c-11.2 23.2-6 52.6-6 52.6s-16.6 19.8-19.8 43.2c-3.2 23.4 6 52.6 6 52.6l36.8 66.2 66.2 16.6c0 0 33.2-13.6 52.6-36.8 19.4-23.2 19.8-52.6 19.8-52.6l36.8-66.2 16.6-16.6c0 0 22.4 19.8 49.8 22.4 27.4 2.6 52.6-13.6 52.6-13.6l16.6-36.8 36.8-16.6c0 0 33.2 13.6 52.6 6 19.4-7.6 33.2-36.8 33.2-36.8l16.6-66.2c0 0 19.8 9.2 43.2 0 23.4-9.2 36.8-33.2 36.8-33.2l66.2-36.8c0 0 13.6-33.2 6-52.6-7.6-19.4-33.2-36.8-33.2-36.8z"
              fill="#FFD700" />
          </svg>
          <div class="speed-lines">
            <span></span><span></span><span></span>
          </div>
        </div>

        <div class="text-area">
          <h1 class="main-title">{{ displayConfig.mainTitle }}</h1>
          <p class="sub-title">{{ displayConfig.subTitle }}</p>

          <div class="loading-bar-container">
            <div class="loading-track"></div>
            <div class="loading-thumb"></div>
          </div>
          <p class="loading-tips">{{ displayConfig.tips }}</p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  newYearConfig: {
    type: Object,
    default: null
  },
  showImmediately: {
    type: Boolean,
    default: false
  }
})

const visible = ref(false)
const hasChecked = ref(false)

// 显示配置（优先使用接口配置，否则使用默认值）
const displayConfig = computed(() => {
  if (props.newYearConfig) {
    return {
      mainTitle: props.newYearConfig.loadingMainTitle || '马到成功',
      subTitle: props.newYearConfig.loadingSubTitle || '2026 丙午年 · 开工大吉',
      tips: props.newYearConfig.loadingTips || '系统正在全速启动...'
    }
  }
  return {
    mainTitle: '马到成功',
    subTitle: '2026 丙午年 · 开工大吉',
    tips: '系统正在全速启动...'
  }
})

const checkShowLoading = () => {
  // 防止重复检查
  if (hasChecked.value) return
  hasChecked.value = true

  const now = new Date()
  now.setHours(0, 0, 0, 0)

  let startDate, endDate

  if (props.newYearConfig) {
    // 使用接口返回的时间配置
    startDate = new Date(props.newYearConfig.startDate)
    endDate = new Date(props.newYearConfig.endDate)
  } else {
    // 使用默认时间配置（2026年春节）
    startDate = new Date('2026-02-14')
    endDate = new Date('2026-02-28')
  }

  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(0, 0, 0, 0)

  // 只有在时间范围内才显示
  if (now >= startDate && now <= endDate) {
    visible.value = true

    // 2.4秒后自动消失
    setTimeout(() => {
      visible.value = false
    }, 2400)
  }
}

// 监听配置变化
watch(() => props.newYearConfig, (newConfig) => {
  if (newConfig && !hasChecked.value) {
    checkShowLoading()
  }
}, { immediate: true })

onMounted(() => {
  // 如果设置了立即显示，先检查一次
  if (props.showImmediately) {
    checkShowLoading()
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Ma+Shan+Zheng&display=swap');

/* 全屏容器：故宫红深色渐变 */
.year-of-horse-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at 50% 30%, #b71c1c 0%, #5d0000 100%);
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ffd700;
  font-family: 'Microsoft YaHei', sans-serif;
  overflow: hidden;
}

/* 背景纹理：淡淡的祥云或噪点 */
.bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: radial-gradient(rgba(255, 215, 0, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.3;
}

.content-wrapper {
  position: relative;
  text-align: center;
  z-index: 2;
}

/* 马的 Logo 容器 */
.horse-logo {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;
  animation: gallopBounce 1s ease-in-out infinite alternate;
}

.horse-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.5));
}

/* 装饰光环 */
.ring-light {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 140px;
  height: 140px;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 50%;
  animation: spin 10s linear infinite;
}

.ring-light::after {
  content: '';
  position: absolute;
  top: -2px;
  left: 50%;
  width: 4px;
  height: 4px;
  background: #ffd700;
  border-radius: 50%;
  box-shadow: 0 0 10px #ffd700;
}

/* 速度线动画：模拟奔跑的风 */
.speed-lines span {
  position: absolute;
  background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.8), transparent);
  height: 2px;
  border-radius: 1px;
  animation: speedLine 1.5s infinite linear;
  opacity: 0;
}

.speed-lines span:nth-child(1) {
  width: 50px;
  top: 40px;
  left: -40px;
  animation-delay: 0s;
}

.speed-lines span:nth-child(2) {
  width: 30px;
  top: 60px;
  left: -20px;
  animation-delay: 0.3s;
}

.speed-lines span:nth-child(3) {
  width: 40px;
  top: 80px;
  left: -30px;
  animation-delay: 0.6s;
}

/* 字体排印 */
.main-title {
  font-family: 'Ma Shan Zheng', cursive, serif;
  /* 书法字体 */
  font-size: 3rem;
  margin: 0;
  background: linear-gradient(to bottom, #ffd700, #ffa000);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  letter-spacing: 4px;
}

.sub-title {
  font-size: 1rem;
  opacity: 0.8;
  margin-bottom: 30px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

/* 极简进度条 */
.loading-bar-container {
  width: 240px;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 auto 10px;
  position: relative;
  overflow: hidden;
  border-radius: 2px;
}

.loading-thumb {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 30%;
  background: #ffd700;
  box-shadow: 0 0 10px #ffd700;
  animation: loadingRun 2s ease-in-out infinite;
}

.loading-tips {
  font-size: 12px;
  opacity: 0.5;
}

/* 动画定义 */
@keyframes spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes gallopBounce {
  0% {
    transform: translateY(0);
  }

  100% {
    transform: translateY(-5px);
  }
}

@keyframes speedLine {
  0% {
    transform: translateX(0);
    opacity: 0;
  }

  50% {
    opacity: 1;
  }

  100% {
    transform: translateX(100px);
    opacity: 0;
  }
}

@keyframes loadingRun {
  0% {
    left: -30%;
  }

  50% {
    left: 100%;
  }

  100% {
    left: 100%;
  }
}

/* 进场/离场过渡 */
.gallop-fade-leave-active {
  transition: all 0.6s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.gallop-fade-leave-to {
  opacity: 0;
  transform: scale(1.1);
  /* 结束时稍微放大，像冲过终点 */
  filter: blur(10px);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .main-title {
    font-size: 2.2rem;
  }

  .horse-logo {
    width: 90px;
    height: 90px;
  }
}
</style>